import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, ProfilePicture, FormTextarea, FormInput } from '@getro/rombo';
import { Box, Flex, Text, Image } from 'rebass/styled-components';
import { Check, ChevronRight } from 'lucide-react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import ArrowLine from 'components/atoms/arrowLine';
import IconLine from '../../../assets/icon-line.svg';
import { RequestIntroReason } from './requestIntroReason';
import { UserNetworkLogo } from './userNetworkLogo';
import { useLocalStorage } from '../../../hooks/useLocalStorage';

const RequestIntroModal = ({
  isOpen,
  onClose,
  selectedContacts,
  user,
  list,
  onRequestIntro,
  isLoading,
  isSingleContact,
}) => {
  const [people, setPeople] = useState(selectedContacts.map((person) => ({ ...person, reason: '' })));
  const [step, setStep] = useState(1);
  const formRef = useRef(null);
  const [userDetails, setUserDetails] = useLocalStorage('user_details', {
    name: user ? `${user.firstName} ${user.lastName}` : '',
    email: user?.email ?? '',
  });

  const handleReasonChange = (id, value) =>
    setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, reason: value } : p)));

  const handleRemove = (id) => setPeople((prev) => prev.filter((p) => p.id !== id));

  const handleNext = async () => {
    if (step !== 2) {
      setStep(step + 1);
      return;
    }

    let response = null;

    if (isSingleContact && people.length > 0) {
      response = await onRequestIntro({
        contactId: people[0].id,
        notes: people[0].reason,
        requesterEmail: formRef.current.values.email,
        requesterName: formRef.current.values.name,
        sharedListId: list.id,
        networkId: list.collection.id,
      });
    }

    if (!isSingleContact) {
      response = await onRequestIntro({
        contacts: people.map((person) => ({
          id: person.id,
          notes: person.reason,
        })),
        requesterEmail: formRef.current.values.email,
        requesterName: formRef.current.values.name,
        notes: formRef.current.values.reason,
        sharedListId: list.id,
        networkId: list.collection.id,
      });
    }

    if (!response?.error) {
      setUserDetails({
        name: formRef.current.values.name,
        email: formRef.current.values.email,
      });
      setStep(step + 1);
    }
  };

  return (
    <Box
      sx={{
        '& [data-testid="header"], & [data-testid="footer"]': { border: 'none' },
        '& [data-testid="footer"]': { pt: '0' },
      }}
    >
      <Modal
        isOpen={isOpen}
        onCancel={onClose}
        title={
          <Flex sx={{ width: '100%', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            {step < 3 && (
              <Box>Request an intro to {people.length === 1 ? people[0].fullName : `${people.length} people`}</Box>
            )}
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
              <Flex>
                <Flex sx={{ alignItems: 'center', flexDirection: 'column', width: '120px', gap: '4px' }}>
                  <Flex
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: 'neutral.100',
                    }}
                  >
                    <Text fontWeight="500" color="text.dark" fontSize="12px" lineHeight="16px">
                      You
                    </Text>
                  </Flex>
                  <Text height="17px" fontWeight="500" color="text.dark" fontSize="12px" lineHeight="16px">
                    You
                  </Text>
                </Flex>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    svg: { color: 'purple.500' },
                    gap: '8px',
                    height: '48px',
                    position: 'relative',
                  }}
                >
                  {step === 3 && (
                    <Box
                      as={Check}
                      sx={{ color: 'purple.500', width: '16px', height: '16px', position: 'absolute', top: '0px' }}
                    />
                  )}
                  <ArrowLine color={step === 3 ? 'purple.500' : 'neutral.300'} />
                </Box>
                <Flex sx={{ alignItems: 'center', flexDirection: 'column', width: '120px', gap: '4px' }}>
                  <UserNetworkLogo
                    user={list.shareableView?.introRequestManager}
                    network={list.collection}
                    width="48px"
                    height="48px"
                  />
                  <Text fontWeight="500" color="text.dark" fontSize="12px" lineHeight="16px">
                    {list.shareableView?.introRequestManager?.fullName}
                  </Text>
                </Flex>
              </Flex>

              <Flex sx={{ gap: '8px', alignSelf: 'flex-end', alignItems: 'center' }}>
                <Flex alignSelf="flex-start">
                  <Image src={IconLine} alt="icon-line" />
                </Flex>
                <Flex sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '4px' }}>
                  <Flex sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    {people.slice(0, people.length > 4 ? 3 : people.length).map((person, index) => (
                      <Box
                        key={person.id}
                        sx={{
                          position: 'relative',
                          zIndex: index,
                          ml: index === 0 ? '0px' : '-24px',
                          borderRadius: '48px',
                          border: '2px solid #FFF',
                        }}
                      >
                        <ProfilePicture
                          width="48px"
                          height="48px"
                          name={person.fullName || person.linkedinHandle || ''}
                          imageUrl={person.profilePicUrl}
                        />
                      </Box>
                    ))}

                    {people.length > 4 && (
                      <Box
                        sx={{
                          position: 'relative',
                          zIndex: 1000,
                          ml: '-24px',
                          borderRadius: '48px',
                          border: '2px solid #FFF',
                        }}
                      >
                        <ProfilePicture
                          width="48px"
                          height="48px"
                          name={`+ ${people.length - 3}`}
                          imageUrl={people[3].profilePicUrl}
                        />
                      </Box>
                    )}
                  </Flex>
                  <Text fontWeight="500" color="text.dark" fontSize="12px" lineHeight="16px">
                    {people.length === 1 ? people[0].fullName || people[0].linkedinHandle : `${people.length} people`}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        }
        actions={
          step < 3 && (
            <Flex sx={{ flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
              <Button
                loading={isLoading}
                variant="primary"
                width="100%"
                justifyContent="center"
                fontWeight="semibold"
                onClick={step === 2 ? () => formRef.current?.handleSubmit() : handleNext}
                icon={step === 1 ? ChevronRight : undefined}
                iconPosition="right"
                iconGap="8px"
              >
                {step === 1 ? 'Next' : `Send to ${list.shareableView?.introRequestManager?.fullName?.split(' ')[0]}`}
              </Button>

              {step === 2 && (
                <Text color="text.subtle" fontSize="14px">
                  Only {list.shareableView?.introRequestManager?.fullName?.split(' ')[0]} will be notified. They will
                  help make the intro(s) happen.
                </Text>
              )}
            </Flex>
          )
        }
        size="md"
      >
        {step === 1 && (
          <RequestIntroReason
            user={user}
            list={list}
            people={people}
            handleReasonChange={handleReasonChange}
            removePerson={handleRemove}
          />
        )}
        {step === 2 && (
          <Formik
            validationSchema={Yup.object().shape({
              name: Yup.string().required('Name is required'),
              email: Yup.string().email('Email is invalid').required('Email is required'),
            })}
            initialValues={{ reason: '', name: userDetails.name ?? '', email: userDetails.email ?? '' }}
            onSubmit={handleNext}
            validateOnMount={false}
            validateOnChange={false}
            autoFocus
            innerRef={formRef}
          >
            <Box as={Form} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Text fontWeight="semibold" fontSize="16px" color="text.dark">
                Step 2: Enter your info
              </Text>
              <Field
                component={FormInput}
                autoFocus
                name="name"
                placeholder="Your name"
                sx={{ maxHeight: '64px', width: '100%' }}
              />
              <Field
                component={FormInput}
                name="email"
                placeholder="Your email"
                sx={{ maxHeight: '64px', width: '100%' }}
              />
              <Field
                component={FormTextarea}
                name="reason"
                placeholder="Optional note"
                sx={{ maxHeight: '64px', width: '100%' }}
              />
            </Box>
          </Formik>
        )}
        {step === 3 && (
          <Flex sx={{ flexDirection: 'column', gap: '24px', alignItems: 'center', pb: '40px' }}>
            <Flex sx={{ flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              <Text fontWeight="600" color="text.dark" fontSize="18px" lineHeight="1.4">
                Intro request sent to {list.shareableView?.introRequestManager?.fullName}
              </Text>
              <Text fontWeight="400" color="text.main" fontSize="16px" lineHeight="1.4" textAlign="center">
                {list.shareableView?.introRequestManager?.fullName} will review your request and help make the intro(s)
                happen.
              </Text>
            </Flex>
            <Button variant="primary" width="80px" justifyContent="center" fontWeight="semibold" onClick={onClose}>
              Ok
            </Button>
          </Flex>
        )}
      </Modal>
    </Box>
  );
};

RequestIntroModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedContacts: PropTypes.array.isRequired,
  user: PropTypes.object,
  list: PropTypes.object.isRequired,
  onRequestIntro: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSingleContact: PropTypes.bool.isRequired,
};

RequestIntroModal.defaultProps = {
  user: null,
};

export default RequestIntroModal;
